import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSiteContent } from "@/lib/content";
import { Reveal, SectionTag, SectionHeading } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const ContactSection = () => {
  const c = useSiteContent();
  const [form, setForm] = useState({ name: "", email: "", reason: "General enquiry", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        message: form.message,
        type: form.reason === "Photography services" ? "service" : "contact",
        service: form.reason === "Photography services" ? "General services enquiry" : null,
      });
      toast.success("Message filed. Expect a reply within 24 hours.");
      setForm({ name: "", email: "", reason: "General enquiry", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const field =
    "bg-[#12151C] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[var(--ekal-accent)] rounded-none";

  return (
    <section id="contact" data-testid="contact-section" className="px-4 sm:px-8 lg:px-16 py-16 lg:py-28 border-t border-white/10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
        <Reveal>
          <SectionHeading>
            Get On <br /> Our <span className="text-[var(--ekal-accent)]">Radar</span>
          </SectionHeading>
          <p className="mt-6 max-w-md text-sm sm:text-base text-slate-400 leading-relaxed">
            {c.contact_intro}
          </p>
          <div className="mt-10 space-y-0 max-w-md">
            {c.contact_rows.map((r) => (
              <div key={r.label} className="flex items-center justify-between border-b border-white/10 py-4">
                <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">{r.label}</span>
                <span className="text-sm font-mono text-slate-200">{r.value}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-4 lg:pt-14" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                data-testid="contact-name-input"
                required
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
              />
              <Input
                data-testid="contact-email-input"
                required
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
              />
            </div>
            <div className="flex gap-2">
              {["General enquiry", "Photography services"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`contact-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setForm({ ...form, reason: r })}
                  className={`flex-1 text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-3 border transition-colors ${
                    form.reason === r
                      ? "border-[var(--ekal-accent)] bg-[var(--ekal-accent)] text-white"
                      : "border-white/15 text-slate-400 hover:text-white"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="contact-message-input"
              required
              rows={6}
              placeholder="Your message…"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={field}
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full bg-[var(--ekal-accent)] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
            >
              {sending ? "Filing…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
